import {EventEmitter} from 'events';

const EVENT_MOUSE_DOWN = 'mousedown';
const EVENT_MOUSE_MOVE = 'mousemove';
const EVENT_MOUSE_UP = 'mouseup';
const EVENT_TOUCH_START = 'touchstart';
const EVENT_TOUCH_MOVE = 'touchmove';
const EVENT_TOUCH_END = 'touchend';
const EVENT_DESKTOP_DRAG = [EVENT_MOUSE_DOWN, EVENT_MOUSE_MOVE, EVENT_MOUSE_UP];
const EVENT_MOBILE_DRAG = [EVENT_TOUCH_START, EVENT_TOUCH_MOVE, EVENT_TOUCH_END];

export class Slider extends EventEmitter {

    /**
     * @param containerSelector
     * @param {{path: string, current: number, draggable: boolean, pictures: string[], keys: boolean, start: number, deltaMove: number, end: number, inverted: boolean, speed: number}} options
     */
    constructor(containerSelector, options) {
        super();
        /** @type {HTMLElement} */
        this.container = document.querySelector(containerSelector);
        this._interval = null;
        this._imgs = [];
        this._prevClientX = null;
        this._isEnabledEvent = true;
        this._nbPictureLoaded = 0;

        /**
         * @type {{path: string, current: number, draggable: boolean, keys: boolean, start: number, deltaMove: number, end: number, inverted: boolean, speed: number}}
         */
        this.options = {
            path: '',
            start: 0,
            end: 0,
            current: 0,
            speed: 100,
            deltaMove: 25,
            pictures: null,
            draggable: true,
            keys: true,
            inverted: false
        };

        this._init(options);
    }

    /**
     * Prepare the slider, load picture
     */
    prepare() {
        this._initContainer();
        this._update();
    }

    /**
     * Merge params attribute and options
     */
    optionAttr() {
        let optionAttr = {};
        for (const index of Object.keys(this.options)) {
            if (this.container.dataset[index]) {
                optionAttr[index] = this.container.dataset[index];
            }
        }


        this.options = Object.assign(this.options, optionAttr);
        const pictures = Object.values(JSON.parse(this.container.dataset.pictures))
        if (pictures.length > 0) {
            this.options.pictures = pictures;
        } else {
            this.options.pictures = this.getPictures();
        }
    }

    getPictures() {
        const pictures = [];
        let currentIndex = this.options.current;
        for (let index = 0; index <= this.options.end - this.options.start; index++) {
            currentIndex++;
            if (currentIndex > this.options.end) {
                currentIndex = this.options.start;
            }
            const pos = currentIndex;
            pictures.push(this._getPathImg(pos));
        }
        return pictures;
    }

    /**
     * next picture
     */
    next() {
        if (this.options.inverted) {
            this.prev();
            return;
        }
        this._currentIndex++;
        if (this._currentIndex > (this.options.end - this.options.start)) {
            this._currentIndex = 0;
        }
        this._update();
    }

    /**
     * Prev picture
     */
    prev() {
        if (this.options.inverted) {
            this.next();
            return;
        }
        this._currentIndex--;
        if (this._currentIndex < 0) {
            this._currentIndex = (this.options.end - this.options.start);
        }
        this._update();
    }

    /**
     * Play automatic rotation
     * @type {Number} nbRotation
     */
    play(nbRotation = -1) {
        let countRotation = 0;
        if (this._interval) {
            this.pause();
        }
        this._interval = setInterval(() => {
            if (this.options.inverted) {
                this.next();
            } else {
                this.prev();
            }
            if (this._currentIndex === 0) {
                countRotation++;
            }
            if (nbRotation !== -1 && nbRotation <= countRotation) {
                this.pause();
            }
        }, this.options.speed);
    }

    reset() {
        this._reset();
        this.goto(0)
    }

    /**
     * Display specific picture
     * @type {Number} pictureIndex
     */
    goto(pictureIndex) {
        this._currentIndex = pictureIndex;
        this._update();
    }

    /**
     * Return current IMG in slider
     * @return {HTMLImageElement}
     */
    getCurrentElement() {
        return this._imgs[this._currentIndex];
    }

    /**
     * Break rotation automatic
     */
    pause() {
        if (this._interval) {
            clearInterval(this._interval);
            this._interval = null;
        }
    }

    /**
     * Enable all event of slider
     */
    enabledEvent() {
        this._isEnabledEvent = true;
    }

    /**
     * Disable all event of slider
     */
    disabledEvent() {
        this._isEnabledEvent = false;
    }

    _init(options) {
        this.optionAttr();
        if (options) {
            this.options = Object.assign(this.options, options);
        }
        this._normaliseOption();

        this.container.addEventListener('click', () => this.pause());

        this._currentIndex = 0;
    }

    /**
     * Convert options
     * @private
     */
    _normaliseOption() {
        this.options.start = Number(this.options.start);
        this.options.end = Number(this.options.end);
        this.options.current = Number(this.options.current) < this.options.start ? this.options.start : Number(this.options.current);
    }

    /**
     * Initialize container, generate all pictures and events
     * @private
     */
    _initContainer() {
        let firstIndex = 0;
        let currentIndex = this.options.current;
        for (let picturePath of this.options.pictures) {
            const img = this._initImg(picturePath, firstIndex === 0);
            img.addEventListener('load', () => {
                this._eventPictureLoaded();
            });
            this._imgs.push(img);
            this.container.insertAdjacentElement('beforeend', img);
            firstIndex++;
        }

        this._initEventDraggable();
        this._initEventKeys();
    }

    _eventPictureLoaded() {
        this._nbPictureLoaded++;
        if (this._nbPictureLoaded === (this.options.end - this.options.start + 1)) {
            this.emit('loaded');
        }
    }

    /**
     * Add event draggable
     * @private
     */
    _initEventDraggable() {
        if (!this.options.draggable) {
            return;
        }

        EVENT_DESKTOP_DRAG.concat(EVENT_MOBILE_DRAG).forEach(eventType => {
            this.container.addEventListener(eventType, event => {
                this._dragUpdate(event);
            });
        });
    }

    /**
     * Add event keys
     * @private
     */
    _initEventKeys() {
        if (!this.options.keys) {
            return;
        }

        this.container.setAttribute('tabindex', '0'); // for keyEvent

        this.container.addEventListener('keydown', event => {
            this._keyUpdate(event);
        });
    }

    /**
     * Generate image element
     * @param path
     * @param show
     * @return {HTMLImageElement}
     * @private
     */
    _initImg(path, show) {
        /** @type {HTMLImageElement} */
        const imgDOM = document.createElement('img');
        imgDOM.src = path;
        imgDOM.style.display = show ? 'block' : 'none';
        imgDOM.classList.add('img-responsive');
        imgDOM.draggable = false;
        return imgDOM;
    }

    /**
     * Generate path picture
     * @param index
     * @return {string}
     * @private
     */
    _getPathImg(index) {
        return this.options.path.split('{index}').join(index);
    }

    /**
     * @type {MouseEvent|TouchEvent} event
     * @private
     */
    _dragUpdate(event) {
        if (!this._isEnabledEvent || event.touches && event.touches.length > 1) {
            return;
        }

        event.preventDefault();

        const clientX = event?.targetTouches?.length > 0 ? event.targetTouches[0].clientX : event.clientX;
        if (event.type === EVENT_MOUSE_DOWN || event.type === EVENT_TOUCH_START) {
            this._prevClientX = clientX;
            return;
        } else if ([EVENT_MOUSE_UP, EVENT_TOUCH_END].includes(event.type)) {
            this._prevClientX = null;
            return;
        }

        if (this._prevClientX === null) {
            return;
        }

        this.pause();
        this._throttle(() => {
            if (this._prevClientX > clientX) {
                this.next();
            } else {
                this.prev();
            }
        }, this.options.deltaMove);

        this._prevClientX = clientX;
    }

    /**
     * @type {KeyboardEvent} event
     * @private
     */
    _keyUpdate(event){
        if (!this._isEnabledEvent) {
            return;
        }
        event.preventDefault();
        if (event.code === 'ArrowLeft') {
            this.prev();
        } else if (event.code === 'ArrowRight') {
            this.next();
        }
    }

    _reset() {
        this._imgs.forEach(img => {
            img.style.display = 'none';
        });
        this._currentIndex = 0;
    }

    /**
     * Update slider view
     * @private
     */
    _update() {
        let nextIndex = this._currentIndex + 1 >= this._imgs.length ? 0 : this._currentIndex + 1;
        let prevIndex = this._currentIndex - 1 < 0 ? this._imgs.length - 1 : this._currentIndex - 1;
        this._imgs[this._currentIndex].style.display = 'block';
        this._imgs[nextIndex].style.display = 'none';
        this._imgs[prevIndex].style.display = 'none';
        this.emit('picture-change', this.options.start + this._currentIndex , this._imgs[this._currentIndex]);
    }

    /**
     * Apply delay for call a function
     * @param callback
     * @param delay
     * @private
     */
    _throttle(callback, delay) {
        const now = (new Date()).getTime();
        if (this.last && now > this.last + delay) {
            this.last = now;
            callback();
        } else if (!this.last) {
            this.last = now;
        }

    }


}
